/**
 * Prompt recall with Up/Down (spec 022, D-6), as in a terminal. Pure: the
 * chat input keeps a `RecallState` and asks these functions what to show.
 */

export interface RecallState {
  /** Index into the recall list while recalling; `null` when not. */
  index: number | null;
  /** The unsent draft from before the first Up, restored past the newest. */
  draft: string;
}

export const NOT_RECALLING: RecallState = { index: null, draft: '' };

/** The conversation's prompts, oldest first, consecutive duplicates collapsed. */
export function recallList(messages: readonly { role: string; text: string }[]): string[] {
  const list: string[] = [];
  for (const m of messages) {
    if (m.role !== 'user') continue;
    if (list[list.length - 1] !== m.text) list.push(m.text);
  }
  return list;
}

/** No selection and the caret before the first newline. */
export function caretOnFirstLine(value: string, selectionStart: number, selectionEnd: number): boolean {
  return selectionStart === selectionEnd && !value.slice(0, selectionStart).includes('\n');
}

/** No selection and the caret after the last newline. */
export function caretOnLastLine(value: string, selectionStart: number, selectionEnd: number): boolean {
  return selectionStart === selectionEnd && !value.slice(selectionEnd).includes('\n');
}

export interface RecallResult {
  state: RecallState;
  value: string;
}

/** Up: the previous prompt (stops at the oldest), or `null` when there is none to recall. */
export function recallUp(list: readonly string[], state: RecallState, current: string): RecallResult | null {
  if (list.length === 0) return null;
  if (state.index === null) {
    const index = list.length - 1;
    return { state: { index, draft: current }, value: list[index] };
  }
  const index = Math.max(0, state.index - 1);
  return { state: { index, draft: state.draft }, value: list[index] };
}

/** Down while recalling: the next prompt, or past the newest the saved draft. `null` when not recalling. */
export function recallDown(list: readonly string[], state: RecallState): RecallResult | null {
  if (state.index === null) return null;
  if (state.index + 1 < list.length) {
    const index = state.index + 1;
    return { state: { index, draft: state.draft }, value: list[index] };
  }
  return { state: NOT_RECALLING, value: state.draft };
}
